/**
 * Internal Scheduled Tasks API Contracts
 *
 * Request/response contracts for the api-key-gated `/internal/scheduled-tasks/*`
 * surface — the machine-callable twin of the Clerk super-admin
 * `/v1/system/scheduled-tasks/*` routes. Both surfaces drive the SAME
 * `scheduledTasksService`; only the credential differs (internal API key here,
 * Clerk super-admin JWT there), mirroring the existing worker-scaling split
 * (`/internal/infrastructure/worker-scaling-config` vs
 * `/v1/system/worker-scaling`).
 *
 * The verify contract has no `/v1` counterpart: it reads the LIVE EventBridge
 * resources and diffs them against the DB row. For an API-destination target
 * the invoked URL lives on the AWS resource, not on `ScheduledTask.endpointPath`
 * — the two diverge silently, and a misrouted task is otherwise
 * indistinguishable from one that never fired.
 */

import { z } from 'zod'
import {
  ScheduledTaskDomainSchema,
  ScheduledTaskEndpointSchema,
  ScheduledTaskExecutionStatusSchema,
  ScheduledTaskSchema
} from '../../schemas/entities/scheduled-task'
import { ListScheduledTaskExecutionsRequestSchema, ListScheduledTaskExecutionsResponseSchema } from '../system'

// ************************************
// ******* CREATE *********************
// ************************************

/**
 * A task name is also an AWS resource-name fragment (`sj-rule-<name>`,
 * `sj-dest-<name>`, `sj-target-<name>`), hence the kebab-case + length bound.
 */
export const InternalScheduledTaskNameSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, {
    message: 'Name must be kebab-case (lowercase alphanumeric with hyphens)'
  })

export const InternalCreateScheduledTaskRequestSchema = z.object({
  name: InternalScheduledTaskNameSchema,
  description: z.string().min(1),
  schedule: z.string().min(1),
  enabled: z.boolean().optional().default(true),
  /**
   * REQUIRED, matching the `/v1` create contract. Both surfaces drive the same
   * `scheduledTasksService`, so requiring a domain on only one of them would
   * leave this lane free to keep minting unclassified rows — the classification
   * would be enforced in one place and merely declared in the other.
   */
  domain: ScheduledTaskDomainSchema,
  endpoint: ScheduledTaskEndpointSchema
})

export type InternalCreateScheduledTaskRequest = z.infer<typeof InternalCreateScheduledTaskRequestSchema>

export const InternalCreateScheduledTaskResponseSchema = ScheduledTaskSchema

export type InternalCreateScheduledTaskResponse = z.infer<typeof InternalCreateScheduledTaskResponseSchema>

// ************************************
// ******* UPDATE *********************
// ************************************

export const InternalUpdateScheduledTaskRequestSchema = z
  .object({
    schedule: z.string().min(1).optional(),
    enabled: z.boolean().optional(),
    description: z.string().min(1).optional(),
    /** Reclassification. Optional because every PATCH field is; it cannot be cleared back to null. */
    domain: ScheduledTaskDomainSchema.optional(),
    endpoint: ScheduledTaskEndpointSchema.optional()
  })
  .refine(
    data =>
      data.schedule !== undefined ||
      data.enabled !== undefined ||
      data.description !== undefined ||
      data.domain !== undefined ||
      data.endpoint !== undefined,
    { message: 'At least one field must be provided' }
  )

export type InternalUpdateScheduledTaskRequest = z.infer<typeof InternalUpdateScheduledTaskRequestSchema>

// ************************************
// ******* LIST ***********************
// ************************************

export const InternalListScheduledTasksResponseSchema = z.object({
  data: z.array(ScheduledTaskSchema),
  meta: z.object({ total: z.number() })
})

export type InternalListScheduledTasksResponse = z.infer<typeof InternalListScheduledTasksResponseSchema>

// ************************************
// ******* RUN ************************
// ************************************

/**
 * `queued` is NOT `ran`. When the target is the SQS lane, `executionId` is the
 * handle that closes that gap: poll `GET /internal/jobs/status/:executionId`
 * (or read `ScheduledTaskExecution`) until it leaves `queued`. It is present
 * whenever the invoked endpoint returned one — every
 * `withExecutionTracking`-wrapped route does.
 */
export const InternalRunScheduledTaskResponseSchema = z.object({
  status: z.number(),
  body: z.unknown(),
  executionId: z.string().optional()
})

export type InternalRunScheduledTaskResponse = z.infer<typeof InternalRunScheduledTaskResponseSchema>

// ************************************
// ******* VERIFY *********************
// ************************************

/**
 * `critical` — the task cannot do the right work: a resource is missing, or it
 * fires at the wrong URL/method, or it carries the wrong body. `warning` — it
 * fires at the right place, but on a cadence or in an enabled-state the DB row
 * does not agree with.
 */
export const ScheduledTaskDriftSeveritySchema = z.enum(['critical', 'warning'])
export type ScheduledTaskDriftSeverity = z.infer<typeof ScheduledTaskDriftSeveritySchema>

export const ScheduledTaskDriftSchema = z.object({
  field: z.string(),
  severity: ScheduledTaskDriftSeveritySchema,
  expected: z.string().nullable(),
  actual: z.string().nullable(),
  message: z.string()
})

export type ScheduledTaskDrift = z.infer<typeof ScheduledTaskDriftSchema>

export const ScheduledTaskAwsStateSchema = z.object({
  rule: z
    .object({
      name: z.string(),
      scheduleExpression: z.string(),
      state: z.string(),
      description: z.string().optional()
    })
    .nullable(),
  destination: z
    .object({
      name: z.string(),
      invocationEndpoint: z.string(),
      httpMethod: z.string(),
      state: z.string().nullable()
    })
    .nullable(),
  target: z
    .object({
      id: z.string(),
      arn: z.string(),
      input: z.string().nullable(),
      queryStringParameters: z.record(z.string(), z.string()).nullable(),
      /** Null means AWS's unbounded default (185 attempts / 24 h) applies to this target. */
      retryPolicy: z
        .object({
          maximumRetryAttempts: z.number().optional(),
          maximumEventAgeInSeconds: z.number().optional()
        })
        .nullable()
    })
    .nullable()
})

export type ScheduledTaskAwsState = z.infer<typeof ScheduledTaskAwsStateSchema>

export const InternalVerifyScheduledTaskResponseSchema = z.object({
  name: z.string(),
  /** True when no drift of ANY severity was found. */
  inSync: z.boolean(),
  /** True when no `critical` drift was found — the task reaches the right endpoint. */
  routedCorrectly: z.boolean(),
  db: z.object({
    schedule: z.string(),
    enabled: z.boolean(),
    endpoint: ScheduledTaskEndpointSchema,
    /** `endpoint.path` resolved against the backend base URL — what AWS SHOULD invoke. */
    resolvedUrl: z.string()
  }),
  aws: ScheduledTaskAwsStateSchema,
  drift: z.array(ScheduledTaskDriftSchema)
})

export type InternalVerifyScheduledTaskResponse = z.infer<typeof InternalVerifyScheduledTaskResponseSchema>

// ************************************
// ******* EXECUTIONS *****************
// ************************************

/**
 * The `/v1` request contract plus a `status` filter. Extended rather than
 * redeclared so the two surfaces cannot drift on pagination bounds — the
 * filter is the only intentional difference, because "has this execution left
 * `queued` yet" is the question that follows every programmatic run.
 */
export const InternalListScheduledTaskExecutionsRequestSchema = ListScheduledTaskExecutionsRequestSchema.extend({
  status: ScheduledTaskExecutionStatusSchema.optional()
})

export type InternalListScheduledTaskExecutionsRequest = z.infer<
  typeof InternalListScheduledTaskExecutionsRequestSchema
>

export const InternalListScheduledTaskExecutionsResponseSchema = ListScheduledTaskExecutionsResponseSchema

export type InternalListScheduledTaskExecutionsResponse = z.infer<
  typeof InternalListScheduledTaskExecutionsResponseSchema
>
